import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Crown, Sparkles, Trophy } from "lucide-react";
import { clubInfo, MbtiBadge } from "@/lib/mbti";

const CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];
const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"]; // Gold, Silver, Bronze

export default function HallOfFame() {
  const [data, setData] = useState(null);
  const [podium, setPodium] = useState(null);
  useEffect(() => {
    api.get("/weekly-mvps/history").then((r) => setData(r.data));
    api.get("/editions/podium/history").then((r) => setPodium(r.data)).catch(() => setPodium({ editions: [] }));
  }, []);

  if (!data) return <div className="kw-overline">A carregar…</div>;

  // Count wins per player to compute "leaders" of the edition
  const wins = {};
  data.history.forEach((wk) => {
    wk.winners.forEach((w) => {
      if (!w.name) return;
      const key = `${w.player_id}|${w.club}`;
      wins[key] = wins[key] || { name: w.name, club: w.club, mbti: w.mbti, total: 0, weeks: [] };
      wins[key].total += 1;
      wins[key].weeks.push(w.week);
    });
  });
  const leaders = Object.values(wins).sort((a, b) => b.total - a.total).slice(0, 6);

  return (
    <div className="space-y-8 kw-fade-up" data-testid="hall-of-fame-page">
      <div className="flex items-end gap-4">
        <Crown className="w-12 h-12 text-[#E50000] mb-1" />
        <div>
          <div className="kw-overline mb-2">Salão dos vencedores</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Hall of <span className="text-[#E50000]">Fame</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm">Histórico semanal dos vencedores e podium por edição.</p>
        </div>
      </div>

      {/* Podium por Edição (Fase 2: snapshot ao fechar cada edição) */}
      {podium && podium.editions && podium.editions.length > 0 && (
        <div className="space-y-4" data-testid="hof-editions">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-[#E50000]" />
            <h2 className="font-display text-3xl font-black uppercase">Podium por Edição</h2>
          </div>
          {podium.editions.map((ed) => (
            <div key={ed.id} className="kw-card p-6" data-testid={`hof-edition-${ed.edition_id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4 border-b border-[#222] pb-3">
                <div>
                  <div className="font-display text-2xl font-black uppercase">{ed.edition_name}</div>
                  <div className="text-xs text-[#737373] mt-1 font-mono">
                    {ed.edition_start_date} → {ed.edition_end_date}
                  </div>
                </div>
                <div className="kw-overline text-[#737373]">Fechada em {ed.closed_at?.slice(0, 10)}</div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {CLUBS.map((club) => {
                  const info = clubInfo(club);
                  const clubPodium = (ed.podiums || []).find((p) => p.club === club);
                  return (
                    <div key={club} className="kw-card p-4" style={{ borderColor: info?.border, borderWidth: 2 }} data-testid={`hof-edition-club-${club}`}>
                      <div className="kw-overline mb-3 flex items-center gap-2" style={{ color: info?.color }}>
                        <Crown className="w-4 h-4" /> {club}
                      </div>
                      {!clubPodium || clubPodium.podium.length === 0 ? (
                        <div className="text-xs text-[#444] py-6 text-center">Sem podium</div>
                      ) : (
                        <ol className="space-y-2">
                          {clubPodium.podium.map((r) => (
                            <li key={r.player_id} className="flex items-center gap-3">
                              <div className="font-display text-2xl font-black w-6 text-center" style={{ color: MEDAL_COLORS[r.rank - 1] || "#666" }}>
                                {r.rank}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">{r.player_name}</div>
                                <div className="text-[10px] text-[#737373] font-mono">{r.player_mbti}</div>
                              </div>
                              <div className="text-right">
                                <div className="font-display text-lg font-black" style={{ color: info?.color }}>{Math.round(r.points)}</div>
                                <div className="kw-overline">pts</div>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {leaders.length > 0 && (
        <div className="kw-card p-6">
          <div className="flex items-center gap-3 mb-4 border-b border-[#222] pb-3">
            <Sparkles className="w-5 h-5 text-[#E50000]" />
            <h2 className="font-display text-2xl font-black uppercase">Mais vezes coroados</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {leaders.map((l) => {
              const info = clubInfo(l.club);
              return (
                <div key={`${l.name}-${l.club}`} className="kw-card p-4" style={{ borderColor: info?.border }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="kw-overline" style={{ color: info?.color }}>{l.club}</div>
                      <div className="font-display text-lg font-black uppercase mt-1">{l.name}</div>
                      <div className="mt-1"><MbtiBadge mbti={l.mbti} compact /></div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-4xl font-black" style={{ color: info?.color }}>{l.total}</div>
                      <div className="kw-overline">vitórias</div>
                    </div>
                  </div>
                  <div className="text-xs text-[#737373] mt-2 font-mono">Semanas: {l.weeks.join(", ")}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="kw-card overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="hof-table">
          <thead className="bg-[#0A0A0A] border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-3">Semana</th>
              {CLUBS.map((c) => {
                const info = clubInfo(c);
                return <th key={c} className="p-3" style={{ color: info?.color }}>{c}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {data.history.length === 0 && (
              <tr><td colSpan={CLUBS.length + 1} className="p-8 text-center text-[#737373]">Sem dados ainda.</td></tr>
            )}
            {data.history.map((wk) => (
              <tr key={wk.week} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                <td className="p-3 font-display text-2xl font-black">{String(wk.week).padStart(2, "0")}</td>
                {CLUBS.map((c) => {
                  const w = wk.winners.find((x) => x.club === c);
                  const info = clubInfo(c);
                  if (!w || !w.name) {
                    return <td key={c} className="p-3 text-[#444]">—</td>;
                  }
                  return (
                    <td key={c} className="p-3">
                      <div className="font-semibold">{w.name}</div>
                      <div className="text-xs flex items-center gap-2 mt-1">
                        <span className="font-mono" style={{ color: info?.color }}>{w.mbti}</span>
                        <span className="text-[#A3A3A3]">·</span>
                        <span className="font-display font-black" style={{ color: info?.color }}>{Math.round(w.points)} pts</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
